import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import uuid from 'src/nativeModules/UUID';
import ScreenContext from '../../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Styles from '../../../Styles';
import type {CartItem, TransactionDetail} from 'src/types/TransactionDetail';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import Settings from 'src/Settings';
import Localized from 'src/constants/AppStrings';
import {alertError, alertSuccess} from '../../../helpers/AlertHelper';
import {TimeSlotType} from 'src/types/Menu';
import {connect, ConnectedProps} from 'react-redux';
import {AppDispatch, RootState} from 'src/redux/store';
import {compose} from 'redux';
import {NavigationProp} from '@react-navigation/native';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {EnvironmentKey} from '../../../../models/Environment';
import BaseScreen from '../../BaseScreen';
import AVText from '../../../elements/AVText';
import Util, {formatDate, getTotalDisplayAmount} from 'src/Util';
import MachineStore from '../../../../stores/MachineStore';
import {CampusLocationType} from 'src/types/CampusLocationTypes';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import PointsEarned from 'src/components/elements/cart/PointsEarned';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../../i18n/HOC/withGlobalize';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

const {bodyFontSize, bodyLargeFontSize, bodyTinyFontSize, mainFontFamily} =
  getLayoutDescriber();

function transactionItems(
  item: CartItem,
  formatCurrency: (value: number) => string,
) {
  return (
    <View style={styles.transactionItemsContainer}>
      <View key={item.Id} style={styles.transactionItemsView}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={item.Description}
          aria-label={item.Description}
          style={[styles.transactionItemsText, {flex: 1}]}
        >
          {item.Description}
        </AVText>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={`${item.Description} ${formatCurrency(
            Number(item.Price),
          )}`}
          aria-label={`${item.Description} ${formatCurrency(
            Number(item.Price),
          )}`}
          style={[styles.transactionItemsText]}
        >
          {formatCurrency(Number(item.Price))}
        </AVText>
      </View>
    </View>
  );
}

type PurchaseReceiptScreenProps = WithGlobalizeProps &
  PropsFromRedux & {
    transaction: TransactionDetail;
    locationId: string;
    transactionId: string;
    transactionType: string;
    checkout: boolean;
    pickupNotes: string;
    marketAccount?: string | null;
    pickupLocation: string;
    pickupTime: TimeSlotType;
    orderNumber: string;
    location: string;
    locationName: string;
    transactionDate: string;
    discount: number;
    env: EnvironmentKey;
    navigation?: NavigationProp<any>;
    transactionCurrency?: string;
    displayBalances?: string;
  };

type PurchaseReceiptScreenState = {
  balance: number;
};
class ScanPay365ReceiptScreen extends React.Component<
  PurchaseReceiptScreenProps,
  PurchaseReceiptScreenState
> {
  receiptLink: string | null | undefined;
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props: PurchaseReceiptScreenProps) {
    super(props);
    const totalAmount = getTotalDisplayAmount(props?.balances);

    this.setState({balance: totalAmount});
    this.state = {
      balance: props.displayBalances || totalAmount,
    };
  }

  getLocationAddress = () => {
    if (this.props.pickupLocation) {
      return this.props.pickupLocation;
    }

    const marketsList =
      MachineStore.getMarkets() as unknown as CampusLocationType[];
    const market = marketsList.find(
      (m) => m.locationId === this.props.locationId,
    );

    if (market) {
      return [
        market.address,
        market.city,
        [market.state, market.zip].filter((x) => x).join(' '),
        market.country,
      ]
        .filter((x) => x)
        .join(', ');
    }

    return '';
  };

  handleEmailReceipt = async () => {
    this.context.actions.showSpinner();
    try {
      const response = await ActionsFactory.getAccountActions().emailReceipt(
        AccountStore.getEmail(),
        this.props.transactionId,
        this.props.transactionType,
        AccountStore.getRegion(),
        AccountStore.getPurchaseHistoryItem(this.props.transactionId)
          ?.Balance ?? 0,
      );

      this.context.actions.hideSpinner();
      if (response.statuscode === 200) {
        FirebaseAnalytic.trackEvent(
          'handleEmailReceipt',
          'ReceiptScreen',
          this.props,
        );
        setTimeout(() => {
          alertSuccess(Localized.Success.email_sent);
        }, 300);
      } else {
        throw new Error('Error sending email receipt');
      }
    } catch (error) {
      this.context.actions.hideSpinner();
      const guid = await uuid.getRandomUUID();
      setTimeout(() => {
        alertError(Localized.Errors.error_emailing_receipt, guid);
      }, 300);
      CrashlyticsEvents.log(
        'Exception',
        'ReceiptScreen:HandleEmailReceipt',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'ReceiptScreen:HandleEmailReceipt',
        generateErrorMessage(error),
        guid,
      );
    }
  };

  handleDone = () => {
    if (Settings.isNewUI() && this.props.checkout) {
      NavActions.reset(AppRoutes.NewHome);
    } else {
      NavActions.popToTop();
    }
  };

  formatCurrency = (value: number) => {
    return Util.formatCurrency(this.props, value, AccountStore.getCurrency());
  };
  render() {
    return (
      <BaseScreen
        hideBack={true}
        accessibilityLabel={Localized.Buttons.back_arrow}
        aria-label={Localized.Buttons.back_arrow}
        accessibilityHint={Localized.Buttons.back_arrow}
        title={Localized.Buttons.receipt}
      >
        <ScrollView style={scanAndPayTemplate.checkoutStyles.receiptContent}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessible={true}
            accessibilityLabel={this.props.locationName}
            accessibilityRole="text"
            aria-label={this.props.locationName}
            numberOfLines={2}
            style={styles.locationName}
          >
            {this.props.locationName}
          </AVText>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessible={true}
            accessibilityLabel={this.props.pickupLocation}
            accessibilityRole="text"
            aria-label={this.props.pickupLocation}
            style={styles.pickupLocation}
          >
            {this.getLocationAddress()}
          </AVText>

          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessible={true}
            accessibilityLabel={this.props.transactionDate}
            accessibilityRole="text"
            aria-label={this.props.transactionDate}
            style={styles.transactionDate}
          >
            {formatDate(this.props.transactionDate, 'MMMM D, YYYY h:mm A')}
          </AVText>

          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessible={true}
            accessibilityLabel={this.props.transactionId}
            accessibilityRole="text"
            aria-label={this.props.transactionId}
            style={styles.transactionId}
          >
            {this.props.transactionId}
          </AVText>

          <PointsEarned points={this.props.transaction.Points} />

          <View style={styles.transactionItemsDot}>
            <View style={styles.transactionItemsViewDot} />
          </View>

          {this.props.transaction.Items.map((item) =>
            transactionItems(item, this.formatCurrency),
          )}

          <View style={styles.transactionItemsDot}>
            <View style={styles.transactionItemsViewDot} />
          </View>

          <View style={styles.subTotalSection}>
            <View style={styles.transactionItemsView}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={Localized.Labels.subtotal}
                aria-label={Localized.Labels.subtotal}
                style={styles.transactionItemsSubtotal}
              >
                {Localized.Labels.subtotal}
              </AVText>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={Localized.Labels.subtotal}
                aria-label={Localized.Labels.subtotal}
                style={styles.transactionItemsSubtotal}
              >
                {this.formatCurrency(Number(this.props.transaction.SubTotal))}
              </AVText>
            </View>

            <View style={styles.transactionItemsView}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={Localized.Labels.tax}
                aria-label={Localized.Labels.tax}
                style={styles.transactionItemsSubtotal}
              >
                {Localized.Labels.tax}
              </AVText>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={`${
                  Localized.Labels.tax
                } ${this.formatCurrency(
                  this.props.transaction.Taxes.reduce(
                    (total, val) => total + +val.TaxAmount,
                    0,
                  ),
                )}`}
                aria-label={`${Localized.Labels.tax} ${this.formatCurrency(
                  this.props.transaction.Taxes.reduce(
                    (total, val) => total + +val.TaxAmount,
                    0,
                  ),
                )}`}
                style={styles.transactionItemsSubtotal}
              >
                {this.formatCurrency(
                  this.props.transaction.Taxes.reduce(
                    (total, val) => total + +val.TaxAmount,
                    0,
                  ),
                )}
              </AVText>
            </View>

            {!scanAndPayTemplate.enableDiscountDisplay && (
              <View style={styles.transactionItemsView}>
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                  accessible={true}
                  accessibilityRole="text"
                  accessibilityLabel={Localized.Labels.discount}
                  aria-label={Localized.Labels.discount}
                  style={styles.discountLabel}
                >
                  {Localized.Labels.discount}
                </AVText>
                <AVText
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
                  accessible={true}
                  accessibilityRole="text"
                  accessibilityLabel={`${
                    Localized.Labels.discount
                  } ${this.formatCurrency(
                    Number(this.props.transaction.PromotionDiscount) * -1,
                  )}`}
                  aria-label={`${
                    Localized.Labels.discount
                  } ${this.formatCurrency(
                    Number(this.props.transaction.PromotionDiscount) * -1,
                  )}`}
                  style={styles.discountLabel}
                >
                  {this.formatCurrency(
                    Number(this.props.transaction.PromotionDiscount) * -1,
                  )}
                </AVText>
              </View>
            )}
          </View>
          <View style={styles.transactionItemsDot}>
            <View style={styles.transactionItemsViewDot} />
          </View>

          <View style={styles.transactionTotalView}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={Localized.Labels.total}
              aria-label={Localized.Labels.total}
              style={styles.transactionTotal}
            >
              {scanAndPayTemplate.totalLabel}
            </AVText>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`${
                Localized.Labels.total
              } ${this.formatCurrency(Number(this.props.transaction.Total))}`}
              aria-label={`${Localized.Labels.total} ${this.formatCurrency(
                Number(this.props.transaction.Total),
              )}`}
              style={styles.transactionTotal}
            >
              {this.formatCurrency(Number(this.props.transaction.Total))}
            </AVText>
          </View>
          <View style={styles.footer} />
        </ScrollView>

        <View style={scanAndPayTemplate.checkoutStyles.floatButton}>
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Outline}
            textStyle={styles.emailReceiptText}
            accessibilityLabelValue={Localized.Labels.email_receipt}
            aria-label={Localized.Labels.email_receipt}
            onPress={this.handleEmailReceipt}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
            titleText={Localized.Labels.email_receipt}
            buttonViewStyle={scanAndPayTemplate.checkoutStyles.emailView}
          />
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            textStyle={styles.doneText}
            accessibilityLabelValue={Localized.Buttons.done}
            aria-label={Localized.Buttons.done}
            onPress={this.handleDone}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
            titleText={Localized.Buttons.done}
            buttonViewStyle={scanAndPayTemplate.checkoutStyles.doneBtnView}
            buttonContainerStyle={styles.doneContainer}
          />
        </View>
      </BaseScreen>
    );
  }
}

const styles = StyleSheet.create({
  footer: {
    height: 100,
  },
  location: {
    marginTop: Styles.Spacing.m3,
    color: Styles.darkColor,
    fontSize: bodyFontSize(),
    fontWeight: '400',
    fontFamily: mainFontFamily(),
  },

  pickupTime: {
    color: Styles.darkColor,
    fontSize: bodyTinyFontSize(),
  },
  orderNumber: {
    color: Styles.lightGray,
    fontSize: bodyTinyFontSize(),
    marginBottom: Styles.Spacing.m2,
  },
  locationName: {
    fontSize: bodyLargeFontSize(),
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
    fontFamily: mainFontFamily(),
  },
  pickupLocation: {
    color: Styles.Colors.PayNew.black01,
    fontSize: bodyTinyFontSize(),
    fontFamily: mainFontFamily(),
    marginTop: Styles.Spacing.m1,
  },
  transactionDate: {
    color: Styles.Colors.PayNew.black01,
    fontSize: bodyFontSize(),
    marginTop: Styles.Spacing.m3,
    fontFamily: mainFontFamily(),
  },
  transactionId: {
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontSize: bodyFontSize(),
    marginBottom: Styles.Spacing.m2,
    fontFamily: mainFontFamily(),
    marginTop: Styles.Spacing.m1 - 2,
  },
  transactionItemsDot: {
    height: 1,
    overflow: 'hidden',
    marginTop: Styles.Spacing.m2,
  },
  transactionItemsViewDot: {
    height: 2,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: Styles.neutralHues,
  },
  transactionTotalView: {
    paddingTop: Styles.Spacing.m2,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  transactionItemsContainer: {
    marginVertical: Styles.Spacing.m1,
  },
  transactionItemsView: {
    paddingTop: Styles.Spacing.m2,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  transactionPromoView: {
    marginTop: Styles.Spacing.m1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionItemsText: {
    color: Styles.Colors.PayNew.black01,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: mainFontFamily(),
  },
  discountLabel: {
    color: scanAndPayTemplate.discountTagColor,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: mainFontFamily(),
  },
  transactionPromoText: {
    color: Styles.Colors.PayNew.success,
    fontSize: bodyLargeFontSize(),
  },
  transactionItemsSubtotal: {
    color: Styles.Colors.PayNew.black01,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: mainFontFamily(),
    fontWeight: '400',
  },
  transactionTotal: {
    fontWeight: 'bold',
    color: Styles.Colors.PayNew.black01,
    fontSize: scanAndPayTemplate.totalFontSize,
    marginTop: Styles.Spacing.m1 - 2,
  },
  subTotalSection: {
    marginVertical: Styles.Spacing.m2 - 2,
  },
  emailReceipt: {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    color: scanAndPayTemplate.btnTextColor,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
  doneText: {
    fontFamily: mainFontFamily(),
    fontWeight: '700',
    fontSize: bodyFontSize(),
    color: Styles.Colors.PayNew.white01,
  },
  doneContainer: {
    marginLeft: Styles.Spacing.m3,
  },
});

const mapStateToProps = (state: RootState) => ({
  displayBalances: state.account.displayBalances,
  balances: state.account.account?.balances ?? [],
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  dispatch,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose<React.ComponentType<any>>(
  withForwardedNavigationParams<PurchaseReceiptScreenProps>(),
  withGlobalize,
  connector,
)(ScanPay365ReceiptScreen);
